import FirstStepButton from "./FirstStepButton";
import ThreadSettingsFields from "./ThreadSettingsFields";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@primitives/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { Input } from "@/components/primitives/input";
import { Button } from "@/components/primitives/button";
import { Switch } from "@/components/primitives/switch";
import { GroupChatFormType, GroupChatFormSchema } from "@/schemas/chat";
import { useTranslation } from "react-i18next";
import { createNewGroupChat } from "@/middleware/chats";
import { useToast } from "@/hooks/useToast";
import AvatarUploader from "@/components/shared/AvatarUploader";
import { useNewGroupChatState } from "@/zustand/NewGroupChatState";
import { useAvatarUploader } from "@/middleware/hooks/useAvatarUploader";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/primitives/scroll-area";
import { MemberRole } from "@/server/types/member";

export default function CreateGroupChatForm() {
  const { isUploading, blobUrl, attachmentId, handleFileChange, deleteAvatar } =
    useAvatarUploader({});
  const usernames = useNewGroupChatState((state) => state.usernames);
  const { toast } = useToast();
  const { t } = useTranslation();
  const form = useForm<GroupChatFormType>({
    resolver: zodResolver(GroupChatFormSchema),
    defaultValues: {
      avatar: "",
      title: "",
      description: "",
      isSearchable: false,
      allowThreads: false,
      canPost: MemberRole.MEMBER,
    },
  });

  async function onSubmit(values: GroupChatFormType) {
    try {
      await createNewGroupChat({
        ...values,
        avatar: attachmentId,
      });
    } catch (error) {
      toast({
        duration: 1000,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    }
  }

  return (
    <div
      id="CreateGroupChatForm"
      className="absolute left-0 top-[env(safe-area-inset-top,0px)] flex h-[calc(100%-env(safe-area-inset-top,0px))] w-full flex-col overflow-hidden bg-card lg:top-0 lg:h-full"
    >
      <div className="flex h-leftColumnHeaderHeight shrink-0 items-center border-b border-primary/20 px-4">
        <FirstStepButton disabled={isUploading} />

        <div className="flex flex-col">
          <h2 className="truncate text-lg font-semibold leading-tight">
            {t("createNewGroupChat")}
          </h2>
          {usernames.length > 0 && (
            <h3 className="truncate text-sm font-normal leading-tight text-muted-2">
              {usernames.length + 1} members
            </h3>
          )}
        </div>
      </div>
      <ScrollArea className="flex h-full flex-1 py-2">
        <div className="flex h-full flex-1 flex-col items-center justify-start gap-1 p-4 align-middle">
          <AvatarUploader
            handleFileChange={handleFileChange}
            handleDeleteAvatar={deleteAvatar}
            isUploading={isUploading}
            blobUrl={blobUrl}
          />

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex w-full flex-1 flex-col gap-3"
            >
              <FormField
                control={form.control}
                name="isSearchable"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between gap-3">
                    <div className="space-y-0.5">
                      <FormLabel>{t("searchable")}</FormLabel>
                      <FormDescription className="text-foreground/50">
                        {t("searchableDescription")}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(checked) => {
                          field.onChange(checked);
                          if (!checked) {
                            form.setValue("description", "");
                          }
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                disabled={form.formState.isSubmitting}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("title")}</FormLabel>
                    <FormControl>
                      <Input
                        className="rounded-lg border-border bg-card py-6 placeholder:opacity-70"
                        placeholder='ex: "Pyramids of Giza"'
                        autoComplete="title"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                disabled={form.formState.isSubmitting}
                name="description"
                render={({ field }) => (
                  <FormItem
                    className={cn(!form.watch("isSearchable") && "opacity-30")}
                  >
                    <FormLabel>{t("description")}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          disabled={!form.watch("isSearchable")}
                          className="rounded-lg border-border bg-card py-6 pr-9 placeholder:opacity-70"
                          type="text"
                          placeholder='ex: "A place where ancient wisdom is shared"'
                          autoComplete="description"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <ThreadSettingsFields form={form} />

              <Button
                disabled={
                  form.formState.isSubmitting ||
                  // !form.formState.isValid ||
                  isUploading
                }
                type="submit"
                className="mt-auto h-12 w-full rounded-lg"
              >
                {form.formState.isSubmitting ? t("saving") : t("save")}
              </Button>
            </form>
          </Form>
        </div>
      </ScrollArea>
    </div>
  );
}
